import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal, Plus, Ticket } from 'lucide-react';
import { useState } from 'react';
import z from 'zod';

import type {
  GetPromoCodes,
  getPromoCodesSchema,
} from '@virtual-care/client';
import { formatDateShort } from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@virtual-care/shadcn';
import {
  ConfirmDialog,
  SearchInput,
  TableRowSort,
  client,
  useAction,
} from '@virtual-care/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import { CreatePromoCode } from './create-promo-code.tsx';
import { UpdatePromoCodeDialog } from './update-promo-code.tsx';

type PromoCodeRow = GetPromoCodes['records'][number];

function getStatusBadge(row: PromoCodeRow) {
  const now = new Date();
  const validFrom = new Date(row.validFrom);
  const validUntil = new Date(row.validUntil);
  if (now < validFrom) {
    return <Badge variant="outline">Scheduled</Badge>;
  }
  if (now > validUntil) {
    return <Badge variant="secondary">Expired</Badge>;
  }
  if (row.usedCount >= row.maxUses) {
    return <Badge variant="destructive">Exhausted</Badge>;
  }
  return <Badge variant="default">Active</Badge>;
}

export default function PromoCodesPage() {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true },
  ]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PromoCodeRow | null>(null);
  const deletePromoCodeMutation = useAction('DELETE /promo-codes/{id}');

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useQuery({
    queryKey: ['promo-codes', pagination, sorting, searchQuery],
    queryFn: () => {
      const [sort] = sorting;
      return client.request('GET /promo-codes', {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        query: searchQuery || undefined,
        sortBy: sort?.id as z.infer<typeof getPromoCodesSchema.shape.sortBy>,
        sortOrder: sort?.desc ? 'desc' : 'asc',
      });
    },
    placeholderData: keepPreviousData,
  });

  const columns: ColumnDef<PromoCodeRow>[] = [
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => (
        <div className="font-mono font-semibold">{row.getValue('code')}</div>
      ),
    },
    {
      accessorKey: 'discountPercent',
      header: 'Discount',
      cell: ({ row }) => `${row.getValue('discountPercent')}%`,
    },
    {
      id: 'usage',
      header: 'Used / Max',
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.usedCount} / {row.original.maxUses}
        </span>
      ),
    },
    {
      accessorKey: 'validFrom',
      header: 'Valid From',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {formatDateShort(new Date(row.getValue('validFrom')))}
        </span>
      ),
    },
    {
      accessorKey: 'validUntil',
      header: 'Valid Until',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {formatDateShort(new Date(row.getValue('validUntil')))}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => getStatusBadge(row.original),
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const promoCode = row.original;
        const canDelete = promoCode.usedCount === 0;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => navigator.clipboard.writeText(promoCode.code)}
              >
                Copy code
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setEditing(promoCode)}>
                Edit promo code
              </DropdownMenuItem>
              <ConfirmDialog
                title="Delete Promo Code"
                description={
                  canDelete ? (
                    <span>
                      Delete{' '}
                      <span className="font-bold">{promoCode.code}</span>? This
                      action cannot be undone.
                    </span>
                  ) : (
                    <span>
                      <span className="font-bold">{promoCode.code}</span> has
                      been redeemed and cannot be deleted. Shorten the validity
                      window to deactivate it instead.
                    </span>
                  )
                }
                onConfirm={async () => {
                  if (!canDelete) return;
                  await deletePromoCodeMutation.mutateAsync({
                    id: promoCode.id,
                  });
                  await dataQuery.refetch();
                }}
              >
                <DropdownMenuItem
                  disabled={!canDelete}
                  onSelect={(e) => e.preventDefault()}
                >
                  Delete promo code
                </DropdownMenuItem>
              </ConfirmDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount || 0,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="w-full">
      <CountablePageHeader
        title="Promo Codes"
        totalCount={`${dataQuery.data?.totalCount || 0} total promo codes`}
        icon={Ticket}
      >
        <CreatePromoCode
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          trigger={
            <Button>
              <Plus className="mr-2 size-4" />
              Add Promo Code
            </Button>
          }
          onSave={() => dataQuery.refetch()}
        />
      </CountablePageHeader>

      {editing && (
        <UpdatePromoCodeDialog
          promoCode={editing}
          trigger={null}
          open={!!editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      )}

      <div className="mb-4">
        <SearchInput
          placeholder="Search promo codes..."
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </TableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No promo codes found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />
    </div>
  );
}
